/**
 * Shared types for the survival game loop. Everything here is plain data so
 * UI, entity, rendering and rules code can all import it without cycles.
 */

/** One enchantment on a stack (ids: game/enchant/registry.ts `E`). */
export interface Ench { id: number; lvl: number }

/** One inventory slot's contents. id 0 (AIR) / count 0 = empty. Always mutate in place. */
export interface Stack {
  /** Block id (< ITEM_BASE) or item id (>= ITEM_BASE). */
  id: number;
  count: number;
  /** Durability used so far (tools/armor); 0 = new. */
  dmg: number;
  /** Enchantments (absent = none). Enchanted books store theirs here too. */
  ench?: Ench[];
  /** Anvil prior-work penalty count (absent = 0). */
  rep?: number;
  /** Custom (anvil) name. */
  name?: string;
  /** Crossbow loaded (saved in the extras tail).*/
  charged?: boolean;
  /** Firework data: rocket [duration, ...star colours], star [colour]; on a loaded crossbow, the rocket in it. */
  fw?: number[];
}

const copyEnch = (e: Ench[] | undefined): Ench[] | undefined => (e && e.length ? e.map((x) => ({ id: x.id, lvl: x.lvl })) : undefined);

export const emptyStack = (): Stack => ({ id: 0, count: 0, dmg: 0 });
export const isEmpty = (s: Stack): boolean => s.id <= 0 || s.count <= 0;
export const cloneStack = (s: Stack): Stack => {
  const c: Stack = { id: s.id, count: s.count, dmg: s.dmg };
  const e = copyEnch(s.ench);
  if (e) c.ench = e;
  if (s.rep) c.rep = s.rep;
  if (s.name) c.name = s.name;
  if (s.charged) c.charged = true;
  if (s.fw) c.fw = s.fw.slice();
  return c;
};
export function setStack(dst: Stack, src: Stack): void {
  dst.id = src.id; dst.count = src.count; dst.dmg = src.dmg;
  const e = copyEnch(src.ench);
  if (e) dst.ench = e; else delete dst.ench;
  if (src.rep) dst.rep = src.rep; else delete dst.rep;
  if (src.name) dst.name = src.name; else delete dst.name;
  if (src.charged) dst.charged = true; else delete dst.charged;
  if (src.fw) dst.fw = src.fw.slice(); else delete dst.fw;
}
export function clearStack(s: Stack): void {
  s.id = 0; s.count = 0; s.dmg = 0;
  delete s.ench; delete s.rep; delete s.name; delete s.charged; delete s.fw;
}
export const stackOf = (id: number, count = 1, dmg = 0): Stack => ({ id, count, dmg });
/** Stable string for "same enchantments" comparisons ('' = none). */
export const enchKey = (s: Stack): string => {
  const e = s.ench && s.ench.length ? s.ench.map((x) => `${x.id}:${x.lvl}`).join(',') : '';
  return e + (s.rep ? `r${s.rep}` : '') + (s.name ? `n${s.name}` : '') + (s.fw ? `f${s.fw.join('.')}` : '');
};

/**
 * Enchantment tail appended to a flat [id, count, dmg]* stack list: a -1 marker,
 * then per enchanted/repaired stack `[index, rep, n, id1, lvl1, ..., idn, lvln]`.
 * Readers that stop after their fixed stack count ignore it (backward compatible).
 */
export function serializeExtras(stacks: readonly Stack[], out: number[]): void {
  let marked = false;
  stacks.forEach((s, i) => {
    if (isEmpty(s) || (!s.ench?.length && !s.rep && !s.charged && !s.fw)) return;
    if (!marked) { out.push(-1); marked = true; }
    const e = s.ench ?? [];
    // rep field: prior-work count * 2 + loaded-crossbow flag
    out.push(i, (s.rep ?? 0) * 2 + (s.charged ? 1 : 0), e.length);
    for (const x of e) out.push(x.id, x.lvl);
  });
  // firework data: a -3 marker, then [index, length, ...values] per stack (read up to the next marker)
  let fwm = false;
  stacks.forEach((s, i) => {
    if (isEmpty(s) || !s.fw) return;
    if (!fwm) { out.push(-3); fwm = true; }
    out.push(i, s.fw.length, ...s.fw);
  });
  // custom names: a -2 marker, then [index, length, ...char codes]
  let named = false;
  stacks.forEach((s, i) => {
    if (isEmpty(s) || !s.name) return;
    if (!marked) { out.push(-1); marked = true; }
    if (!named) { out.push(-2); named = true; }
    out.push(i, s.name.length);
    for (let k = 0; k < s.name.length; k++) out.push(s.name.charCodeAt(k));
  });
}

/** Read a tail written by serializeExtras starting at `at` (the -1 marker). */
export function deserializeExtras(stacks: readonly Stack[], flat: readonly number[], at: number): void {
  if (flat[at] !== -1) return;
  let p = at + 1;
  while (p + 2 < flat.length && flat[p] !== -2 && flat[p] !== -3) {
    const i = flat[p] | 0, rep = flat[p + 1] | 0, n = flat[p + 2] | 0;
    p += 3;
    const s = stacks[i];
    const e: Ench[] = [];
    for (let k = 0; k < n && p + 1 < flat.length; k++, p += 2) e.push({ id: flat[p] | 0, lvl: flat[p + 1] | 0 });
    if (!s || isEmpty(s)) continue;
    if (e.length) s.ench = e;
    if (rep >> 1 > 0) s.rep = rep >> 1;
    if (rep & 1) s.charged = true;
  }
  if (flat[p] === -3) {
    p++;
    while (p + 1 < flat.length && flat[p] >= 0) {
      const i = flat[p] | 0, len = flat[p + 1] | 0;
      const s = stacks[i];
      if (s && !isEmpty(s)) s.fw = flat.slice(p + 2, p + 2 + len).map((v) => v | 0);
      p += 2 + len;
    }
  }
  if (flat[p] !== -2) return;
  p++;
  while (p + 1 < flat.length) {
    const i = flat[p] | 0, len = flat[p + 1] | 0;
    p += 2;
    const name = String.fromCharCode(...flat.slice(p, p + len));
    p += len;
    const s = stacks[i];
    if (s && !isEmpty(s) && name) s.name = name;
  }
}

export type GameMode = 'survival' | 'creative';

export type DamageKind =
  | 'melee' | 'player' | 'projectile' | 'explosion' | 'fall' | 'fire' | 'lava' | 'drown'
  | 'starve' | 'void' | 'cactus' | 'generic' | 'magic' | 'sonic_boom';

export interface DamageSource {
  kind: DamageKind;
  /** World position the damage came from (for knockback and the hurt camera). */
  x?: number; y?: number; z?: number;
  /** Entity id of the attacker, when there is one. */
  attackerId?: number;
}

export type MobType =
  | 'zombie' | 'skeleton' | 'creeper' | 'spider' | 'pig' | 'cow' | 'sheep' | 'chicken'
  // nether (src/entities/nether)
  | 'zombified_piglin' | 'piglin' | 'magma_cube'
  | 'ghast' | 'blaze' | 'wither_skeleton' | 'hoglin' | 'strider' | 'enderman'
  // villages & illagers (src/entities/village)
  | 'villager' | 'iron_golem' | 'pillager' | 'vindicator' | 'zombie_villager'
  // overworld hostiles round 2 (src/entities/overworld2)
  | 'slime' | 'cave_spider' | 'husk' | 'stray' | 'drowned' | 'silverfish' | 'endermite'
  // tameables (src/entities/overworld2/wolf.ts)
  | 'wolf'
  // witch (src/entities/overworld2/witch.ts)
  | 'witch' | 'phantom' | 'cat'
  // riding (src/entities/riding)
  | 'horse' | 'donkey'
  // aquatic / ambient (src/entities/overworld2/aquatic.ts)
  | 'squid' | 'cod' | 'salmon' | 'bat'
  // jungle (src/entities/overworld2/jungle.ts)
  | 'panda' | 'parrot' | 'fox'
  // wandering trader + llamas (src/entities/trader)
  | 'wandering_trader' | 'llama' | 'trader_llama'
  // bosses (src/entities/boss)
  | 'wither' | 'warden'
  // roster 3 (src/entities/mobs3): animals, monument, mansion, end city
  | 'rabbit' | 'goat' | 'turtle' | 'mooshroom' | 'polar_bear' | 'ocelot' | 'snow_golem'
  | 'axolotl' | 'dolphin' | 'guardian' | 'elder_guardian'
  | 'shulker' | 'shulker_bullet' | 'evoker' | 'evoker_fangs' | 'vex' | 'ravager' | 'zoglin' | 'piglin_brute';

/**
 * What the renderer needs to draw one mob, produced by the entity system every
 * tick/frame. Yaw convention is the player's: forward = (-sin yaw, 0, -cos yaw),
 * radians. Positions are the feet (bottom centre of the hitbox); `px/py/pz` is
 * the previous tick's position so the renderer can interpolate with `alpha`.
 */
export interface MobView {
  readonly id: number;
  readonly type: MobType;
  x: number; y: number; z: number;
  px: number; py: number; pz: number;
  /** Body yaw and head yaw (head turns independently within ~75° of the body). */
  yaw: number; headYaw: number; pitch: number;
  /** Vanilla walk animation: phase and (smoothed) amplitude 0..1. */
  limbSwing: number; limbSwingAmount: number;
  /** Ticks left of the red hurt flash (10 -> 0). */
  hurtTime: number;
  /** Ticks since death (0 while alive; the corpse tips over for 20 ticks then is removed). */
  deathTime: number;
  /** Creeper swell 0..1 (white flash + inflate); other mobs 0. */
  swell: number;
  /** Arm swing / attack progress 0..1 (0 = idle). */
  attackAnim: number;
  onFire: boolean;
  /** Item id held in the main hand (skeleton bow), 0 = none. */
  heldItem: number;
  /** Sheep: sheared. Others ignore. */
  sheared: boolean;
  /** Sheep wool colour index (0 = white .. 15); others ignore. */
  color: number;
  /** Skeleton is drawing its bow / zombie arms raised (aggressive pose). */
  aggressive: boolean;
  /** Wither: wither armour (<= half health, arrow-proof). Also encoded in `color` (bit 256; bits 0-255 = spawn invulnerable ticks / 2) so replicas see it. */
  armored?: boolean;
  /** Armour item ids helmet..boots on a zombie / skeleton (the renderer does not draw them yet). */
  armor?: number[];
  /** Name-tag name drawn as a plate above the mob. */
  name?: string;
  /** Baby animal (half size, big head). Absent = adult. */
  baby?: boolean;
  /** Sheep grazing animation ticks left (40 -> 0). Others ignore. */
  eat?: number;
  /** Wears a saddle (pig, strider, horse). */
  saddled?: boolean;
  /** Horses: coat colour 0..6 | markings 0..4 << 3, 8 = donkey; horse armour tier 0..4 in bits 8-10. */
  coat?: number;
  /** A rider is sitting on it. */
  ridden?: boolean;
  /** Invisible (wandering trader under a Potion of Invisibility): the renderer skips the model. */
  invisible?: boolean;
  /** 0..1 brightness from world light at the mob (sky*daylight and block light). */
  light: number;
}

/** A flying or stuck arrow. */
export interface ArrowView {
  readonly id: number;
  x: number; y: number; z: number;
  px: number; py: number; pz: number;
  yaw: number; pitch: number;
  /** In a block (no motion, no trail). */
  stuck: boolean;
  light: number;
  /** Mob-thrown trident: drawn by the TridentRenderer instead of as an arrow. */
  trident?: boolean;
  enchanted?: boolean;
}

/** A dropped item lying in the world. */
export interface ItemEntityView {
  readonly id: number;
  stack: Stack;
  x: number; y: number; z: number;
  px: number; py: number; pz: number;
  /** Ticks alive; drives the bobbing/spin phase. */
  age: number;
  light: number;
}

/** Third-person / inventory-preview player model state. */
export interface PlayerPoseView {
  /** lying in a bed: `yaw` points at the foot end (head on the pillow) */
  sleeping?: boolean;
  x: number; y: number; z: number;
  px: number; py: number; pz: number;
  yaw: number; headYaw: number; pitch: number;
  limbSwing: number; limbSwingAmount: number;
  /** Arm swing progress 0..1 (0 = idle), vanilla attackAnim. */
  swing: number;
  sneaking: boolean;
  sprinting: boolean;
  swimming: boolean;
  flying: boolean;
  /** Seated on a rideable (game/riding): the third-person sitting pose. */
  sitting?: boolean;
  /** Elytra worn in the chest slot (net layer must send this per player). */
  elytra?: boolean;
  /** Elytra open-glide pose; `vy` = vertical speed (blocks/tick) for wing flutter. */
  gliding?: boolean;
  vy?: number;
  /** Broken elytra (1 durability left) shows the broken texture. */
  elytraBroken?: boolean;
  /** Bow being drawn (arm raised). */
  usingBow: boolean;
  /** Vanilla use-anim pose override (trident / crossbow). */
  useAnim?: '' | 'spear' | 'spear_charge' | 'spear_tired' | 'spear_drop' | 'crossbow_charge' | 'crossbow_hold' | 'eat';
  hurtTime: number;
  /** Main-hand and armor item ids (0 = none): helmet, chest, legs, boots. */
  heldItem: number;
  armor: [number, number, number, number];
  onFire: boolean;
  light: number;
}
